"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useAddToCartSliderStore } from "@/app/store/cart/add-to-cart-slider-store";
import {
	AddToCartSlider,
	AddToCartSliderContent,
	CartSubtotal
} from "@/composites/cart";
import { PromotionProgressBarBlock } from "@/app/components/shared/promotion-progress-bar";
import { RecommendedProductsBlock } from "@/app/components/shared/recommended-products";
import type { Locale } from "@/domain";

const CURRENCY_BY_LOCALE: Record<Locale, string> = {
	"en-US": "USD",
	"en-CA": "CAD"
};

export function AddToCartSliderRoot(): JSX.Element | null {
	const store = useAddToCartSliderStore();
	const locale = useLocale() as Locale;
	const currency = CURRENCY_BY_LOCALE[locale];
	let router: ReturnType<typeof useRouter> | null = null;

	try {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		router = useRouter();
	} catch (e) {
		// Storybook / non-Next.js context fallback
	}

	const handleClose = (): void => {
		store.close();
	};

	const handleContinueShopping = (): void => {
		store.close();
	};

	const handleViewCartAndCheckout = (): void => {
		store.close();
		if (router) {
			router.push("/cart");
		} else {
			window.location.href = "/cart";
		}
	};

	if (!store.addedProduct) {
		return null;
	}

	return (
		<AddToCartSlider
			isOpen={store.isOpen}
			onClose={handleClose}
			onContinueShopping={handleContinueShopping}
			onViewCartAndCheckout={handleViewCartAndCheckout}
			footer={
				<React.Fragment key={store.addedProduct.sku}>
					<PromotionProgressBarBlock
						locale={locale}
						currency={currency}
					/>
					<CartSubtotal locale={locale} currency={currency} />
				</React.Fragment>
			}
		>
			<AddToCartSliderContent
				addedProduct={store.addedProduct}
				locale={locale}
				currency={currency}
			/>

			<RecommendedProductsBlock
				key={store.addedProduct.sku}
				source="ATC"
				sku={store.addedProduct.sku}
				locale={locale}
				variant="compact"
				title="Frequently bought together"
			/>
		</AddToCartSlider>
	);
}
